import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Eye, EyeOff, Loader2, Mail, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { API_BASE } from "../lib/apiConfig";
import { addBreadcrumb, captureError } from "../lib/sentry";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedRedirect = searchParams.get("redirect") || "/explore";
  const redirectTo = requestedRedirect.startsWith("/") && !requestedRedirect.startsWith("//")
    ? requestedRedirect
    : "/explore";
  const verifyResult = searchParams.get("verify");
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    setUnverified(false);
    addBreadcrumb("Login submitted", "auth.login", { rememberMe, redirectTo });
    try {
      await login(email, password, rememberMe);
      addBreadcrumb("Login succeeded", "auth.login", { redirectTo });
      toast.success("Welcome back!");
      navigate(redirectTo);
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || "Login failed. Please try again.";
      if (status === 403) {
        addBreadcrumb("Login blocked by unverified email", "auth.login", { redirectTo }, "warning");
        setUnverified(true);
      } else {
        if (!status || status >= 500) {
          captureError(error, { action: "login", redirectTo });
        }
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/resend-verification`, { email });
      toast.success(res.data.message || "Verification email sent!");
    } catch (error) {
      captureError(error, { action: "resend_verification_from_login" });
      toast.error("Failed to resend. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="fixed inset-0 paper-texture pointer-events-none" />
      
      <header className="p-4">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/icon-192.png" alt="Culture Ken" className="w-10 h-10 rounded-xl" />
          <span className="text-xl font-bold text-foreground">Culture Ken</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-2 text-center">
            <CardTitle className="text-3xl font-bold">Begin your cultural audio adventure</CardTitle>
          </CardHeader>
          <CardContent>
            {verifyResult === "success" && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl" data-testid="verify-success-banner">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800 dark:text-green-200">Email verified!</p>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">Your account is ready. Sign in below.</p>
                  </div>
                </div>
              </div>
            )}

            {verifyResult === "expired" && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl" data-testid="verify-expired-banner">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">Verification link expired</p>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">Sign in below and we'll send you a new code.</p>
                  </div>
                </div>
              </div>
            )}

            {unverified && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl" data-testid="unverified-banner">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Email not verified</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      Please verify your email to sign in. Check your inbox for a 6-digit code.
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Button
                        variant="link"
                        className="p-0 h-auto text-sm text-amber-700 dark:text-amber-300 underline"
                        onClick={() => navigate(`/verify-email?email=${encodeURIComponent(email)}`)}
                        data-testid="goto-verify-from-login-btn"
                      >
                        Enter verification code
                      </Button>
                      <span className="text-amber-400">|</span>
                      <Button
                        variant="link"
                        className="p-0 h-auto text-sm text-amber-700 dark:text-amber-300 underline"
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        data-testid="resend-from-login-btn"
                      >
                        {resendLoading ? "Sending..." : "Resend code"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 text-base"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-base">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 text-base pr-12"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    data-testid="remember-me-checkbox"
                  />
                  <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                    Remember me on this device
                  </Label>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-12 text-lg rounded-xl font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Signing in...</>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground">or</span></div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base rounded-xl font-medium gap-3"
              onClick={() => {
                sessionStorage.setItem("post_auth_redirect", redirectTo);
                const redirect = `${window.location.origin}/auth/callback`;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
              }}
              data-testid="google-signin-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign in with Google
            </Button>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                Don't have an account?{" "}
                <Link 
                  to={`/signup${redirectTo !== "/explore" ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`}
                  className="text-primary font-medium hover:underline"
                  data-testid="goto-signup-link"
                >
                  Register
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LoginPage;
